import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import store from '../../../redux/store';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon
} from '@ionic/react';
import { close, settings, eye, send } from 'ionicons/icons';
import { Wizard } from '../../../components/Wizard/Wizard';
import { WizardStep } from '../../../components/Wizard/WizardStep';
import CalculateInvoiceStep from './CalculateInvoiceStep';
import PreviewInvoiceStep from './PreviewInvoiceStep';
import SendInvoiceStep from './SendInvoiceStep';
import _ from 'lodash';
import moment from 'moment';
import { Mixpanel } from '../../../redux/Mixpanel';
import {
    fetchOptions,
    fetchCurrency,
    showLoading,
    showCustomMessage,
    calculateBulkInvoices,
    createBulkInvoices,
    sendInvoice,
    fetchInvoices,
    fetchPropertiesUnitsTenants
} from '../../../redux/actions';
import {
    getPropertyOptions,
    getSelectedPropertyIds,
    getFilteredPreviewInvoices,
    getUniquePropertiesFromPreview,
    getMonthRange,
    transformCalculatedInvoices,
    buildDuplicateWarningsMap,
    buildInvoicePayload,
    transformInvoiceInputs
} from './utils';
import { UNIFORM_RESOURCE_LOCATORS, NOTIFICATION_TYPES } from '../../../utils/constants';

const SEQUENCE = ["Setup", "Preview", "Send"];

const BulkCreateInvoiceWizard = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const notificationContext = location?.state?.notificationContext || null;
    const hasTrackedNotification = useRef(false);
    
    const properties = useSelector(state => state.properties);
    const units = useSelector(state => state.units);
    const tenants = useSelector(state => state.tenants);
    const invoices = useSelector(state => state.invoices);
    const options = useSelector(state => state.options);
    const currency = useSelector(state => state.currency);
    const settingsState = useSelector(state => state.settings);

    const [currentStep, setCurrentStep] = useState(_.first(SEQUENCE));
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [selectAllProperties, setSelectAllProperties] = useState(false);
    const [invoiceDate, setInvoiceDate] = useState('');
    const [rentSelected, setRentSelected] = useState(false);
    const [otherSelected, setOtherSelected] = useState(false);
    const [penaltiesSelected, setPenaltiesSelected] = useState(false);
    const [previewInvoices, setPreviewInvoices] = useState([]);
    const [duplicateWarningsMap, setDuplicateWarningsMap] = useState({});
    const [searchText, setSearchText] = useState('');
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [createdInvoiceIds, setCreatedInvoiceIds] = useState([]);
    const [deliveryOption, setDeliveryOption] = useState('now');
    const [scheduleDate, setScheduleDate] = useState('');

    useEffect(() => {
        const loadData = async () => {
            dispatch(showLoading(true));
            await Promise.all([
                (async () => {
                    if (_.isEmpty(properties) || _.isEmpty(units)) {
                        await dispatch(fetchPropertiesUnitsTenants());
                    }
                })(),
                (async () => {
                    if (_.isEmpty(options)) {
                        await dispatch(fetchOptions());
                    }
                })(),
                (async () => {
                    if (_.isEmpty(currency)) {
                        await dispatch(fetchCurrency());
                    }
                })()
            ]);

            const opts = getPropertyOptions(properties);
            let props = [];
            if (opts.length === 1) {
                props = [String(opts[0].id)];
            }
            setPropertyOptions(opts);
            setSelectedProperties(props);
            setSelectAllProperties(props.length === opts.length && opts.length > 0);
            dispatch(showLoading(false));
        };
        loadData();
    }, [dispatch, properties, units, options, currency]);

    useEffect(() => {
        if (!_.isEmpty(properties)) {
            const opts = getPropertyOptions(properties);
            const selectedIds = new Set(selectedProperties.map(p => String(p)));
            const props = opts.filter(opt => selectedIds.has(String(opt.id))).map(opt => String(opt.id));
            setPropertyOptions(opts);
            if (props.length !== selectedProperties.length || !props.every((p, i) => String(p) === String(selectedProperties[i]))) {
                setSelectedProperties(props);
            }
            setSelectAllProperties(props.length === opts.length && opts.length > 0);
        }
    }, [properties, selectedProperties]);

    const handleClose = useCallback(() => {
        if (location.state?.returnToInvoices) {
            history.push(UNIFORM_RESOURCE_LOCATORS.invoices);
            return;
        }
        history.goBack();
    }, [history, location.state]);

    const _prevStep = useCallback(() => {
        if (currentStep === SEQUENCE[SEQUENCE.length - 1]) {
            return;
        }
        const index = SEQUENCE.indexOf(currentStep);
        setCurrentStep(SEQUENCE[index - 1]);
    }, [currentStep]);

    const _nextStep = useCallback(() => {
        const index = SEQUENCE.indexOf(currentStep);
        setCurrentStep(SEQUENCE[index + 1]);
    }, [currentStep]);

    const _changeStep = useCallback((stepId) => {
        if (currentStep === SEQUENCE[SEQUENCE.length - 1]) {
            return;
        }
        if (stepId === SEQUENCE[SEQUENCE.length - 1] && createdInvoiceIds.length === 0) {
            return;
        }
        setCurrentStep(stepId);
    }, [currentStep, createdInvoiceIds.length]);

    const filteredPreviewInvoices = useMemo(() => {
        return getFilteredPreviewInvoices(previewInvoices, searchText, selectedPropertyId);
    }, [previewInvoices, searchText, selectedPropertyId]);

    const uniquePropertiesFromPreview = useMemo(() => {
        return getUniquePropertiesFromPreview(previewInvoices);
    }, [previewInvoices]);

    const handleSelectAll = useCallback((checked) => {
        setSelectedProperties(checked ? propertyOptions.map(p => String(p.id)) : []);
        setSelectAllProperties(checked);
    }, [propertyOptions]);

    const handleStepOneContinue = useCallback(async () => {
        if (selectedProperties.length === 0) {
            dispatch(showCustomMessage('', 'Please select at least one property.'));
            return;
        }
        if (!invoiceDate) {
            dispatch(showCustomMessage('', 'Please select an invoice date.'));
            return;
        }
        if (!rentSelected && !otherSelected && !penaltiesSelected) {
            dispatch(showCustomMessage('', 'Please select at least one invoice type.'));
            return;
        }

        const propertyIds = getSelectedPropertyIds(selectedProperties);
        dispatch(showLoading(true));
        try {
            const invoiceItems = [];
            if (rentSelected) invoiceItems.push('rent');
            if (otherSelected) invoiceItems.push('other_recurring_bills');
            if (penaltiesSelected) invoiceItems.push('penalty');
            
            const calcAction = await dispatch(calculateBulkInvoices({ 
                propertyIds, 
                invoiceDate, 
                invoiceItems 
            }));
            
            if (calcAction.errors) {
                return;
            }
            
            const calculated = calcAction.payload?.calculatedInvoices || [];
            const preview = transformCalculatedInvoices(calculated);

            let duplicateMap = {};
            if (rentSelected || otherSelected) {
                const { startDate, endDate } = getMonthRange(invoiceDate);
                await dispatch(fetchInvoices({
                    tenantIds: [],
                    propertyIds,
                    unitIds: [],
                    startDate: moment(startDate),
                    endDate: moment(endDate),
                    status: [],
                    invoiceItem: 'rent',
                    offset: '',
                    limit: 200
                }));
                
                // Get updated invoices from Redux store after fetch
                const updatedInvoices = store.getState().invoices;
                duplicateMap = buildDuplicateWarningsMap(updatedInvoices);
            }

            setPreviewInvoices(preview);
            setDuplicateWarningsMap(duplicateMap);
            _nextStep();
        } finally {
            dispatch(showLoading(false));
        }
    }, [selectedProperties, invoiceDate, rentSelected, otherSelected, penaltiesSelected, dispatch, _nextStep]);

    const handleEditAmount = useCallback((tenantId, value, itemIndex = null) => {
        const amount = value === '' ? 0 : Number(value);
        setPreviewInvoices(prev => prev.map(inv => {
            if (inv.tenantId === tenantId) {
                if (inv.items && inv.items.length > 0 && itemIndex !== null && itemIndex < inv.items.length) {
                    const updatedItems = [...inv.items];
                    updatedItems[itemIndex] = { ...updatedItems[itemIndex], amount };
                    const totalAmount = updatedItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
                    return { ...inv, items: updatedItems, amount: totalAmount };
                } else if (inv.items && inv.items.length > 0) {
                    const updatedItems = inv.items.map(item =>
                        item.name.toLowerCase() === 'rent' ? { ...item, amount } : item
                    );
                    const totalAmount = updatedItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
                    return { ...inv, items: updatedItems, amount: totalAmount };
                } else {
                    return { ...inv, amount };
                }
            }
            return inv;
        }));
    }, []);

    const handleEditDate = useCallback((tenantId, value) => {
        setPreviewInvoices(prev => prev.map(inv => 
            inv.tenantId === tenantId ? { ...inv, invoiceDate: value } : inv
        ));
    }, []);

    const handleDeletePreviewRow = useCallback((tenantId) => {
        setPreviewInvoices(prev => prev.filter(inv => inv.tenantId !== tenantId));
    }, []);

    const handleDeleteInvoiceItem = useCallback((tenantId, itemIndex) => {
        setPreviewInvoices(prev => {
            const updatedInvoices = prev.reduce((acc, inv) => {
                if (inv.tenantId !== tenantId) {
                    acc.push(inv);
                    return acc;
                }
                if (inv.items && inv.items.length > itemIndex) {
                    const updatedItems = inv.items.filter((_, index) => index !== itemIndex);
                    if (updatedItems.length === 0) {
                        return acc;
                    }
                    const totalAmount = updatedItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
                    acc.push({ ...inv, items: updatedItems, amount: totalAmount });
                    return acc;
                }
                if (!inv.items && itemIndex === 0) {
                    return acc;
                }
                acc.push(inv);
                return acc;
            }, []);
            return updatedInvoices;
        });
    }, []);

    const handleGenerate = useCallback(async () => {
        if (filteredPreviewInvoices.length === 0) {
            dispatch(showCustomMessage('', 'No invoices to generate.'));
            return;
        }
        
        const invoicesPayload = buildInvoicePayload(filteredPreviewInvoices);
        
        if (invoicesPayload.length === 0) {
            dispatch(showCustomMessage('', 'No valid invoices to generate.'));
            return;
        }
        
        if (!hasTrackedNotification.current
            && notificationContext?.notification_type === NOTIFICATION_TYPES.invoice_generation_reminder) {
            Mixpanel.track("reminder: invoice_generation primary_action", {
                notification_type: notificationContext.notification_type,
                action: "generate_invoices",
                actionId: notificationContext.actionId || "unknown",
                source: notificationContext.source || "unknown"
            });
            hasTrackedNotification.current = true;
        }

        dispatch(showLoading(true));
        try {
            const invoiceInputs = transformInvoiceInputs(invoicesPayload);
            
            const action = await dispatch(createBulkInvoices(invoiceInputs));
            if (!action.errors) {
                const returnedInvoices = action.payload?.newInvoices || [];
                const invoiceIds = returnedInvoices.map(inv => inv.invoiceId);
                setCreatedInvoiceIds(invoiceIds);
                _nextStep();
            }
        } finally {
            dispatch(showLoading(false));
        }
    }, [filteredPreviewInvoices, dispatch, _nextStep, notificationContext]);

    const onSendInvoices = useCallback(async () => {
        if (createdInvoiceIds.length === 0) {
            dispatch(showCustomMessage('', 'No invoices were created.'));
            return;
        }

        if (deliveryOption === 'schedule' && !scheduleDate) {
            dispatch(showCustomMessage('', 'Please select a date for scheduled delivery.'));
            return;
        }

        const sendDate = deliveryOption === 'schedule' ? scheduleDate : '';

        dispatch(showLoading(true));
        try {
            await dispatch(sendInvoice({ invoiceIds: createdInvoiceIds, sendDate }));
            setTimeout(() => handleClose(), 2000);
        } finally {
            dispatch(showLoading(false));
        }
    }, [createdInvoiceIds, deliveryOption, scheduleDate, dispatch, handleClose]);

    const isStepComplete = useCallback((stepIndex) => {
        switch (stepIndex) {
            case 0:
                return selectedProperties.length > 0 && invoiceDate;
            case 1:
                return createdInvoiceIds.length > 0;
            case 2:
                return false;
            default:
                return false;
        }
    }, [selectedProperties.length, invoiceDate, createdInvoiceIds.length]);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Bulk Create Invoices</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={handleClose}>
                            <IonIcon icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <div className="">
                    <Wizard
                        activeStep={currentStep}
                        onStepChanged={_changeStep}
                    >
                        <WizardStep
                            id={SEQUENCE[0]}
                            icon={<IonIcon icon={settings} />}
                            complete={isStepComplete(0)}
                        >
                            Setup
                        </WizardStep>

                        <WizardStep
                            id={SEQUENCE[1]}
                            icon={<IonIcon icon={eye} />}
                            complete={isStepComplete(1)}
                        >
                            Preview & Generate
                        </WizardStep>

                        <WizardStep
                            id={SEQUENCE[2]}
                            icon={<IonIcon icon={send} />}
                            complete={isStepComplete(2)}
                        >
                            Send Invoices
                        </WizardStep>
                    </Wizard>

                    {currentStep === SEQUENCE[0] && (
                        <CalculateInvoiceStep
                            options={options}
                            propertyOptions={propertyOptions}
                            properties={selectedProperties}
                            selectAllProperties={selectAllProperties}
                            invoiceDate={invoiceDate}
                            rentSelected={rentSelected}
                            otherSelected={otherSelected}
                            penaltiesSelected={penaltiesSelected}
                            onInvoiceDateChange={setInvoiceDate}
                            onPropertiesChange={(selected) => {
                                const normalized = Array.isArray(selected) ? selected.map(s => String(s)) : [];
                                setSelectedProperties(normalized);
                                setSelectAllProperties(normalized.length === propertyOptions.length && propertyOptions.length > 0);
                            }}
                            onRentToggle={(checked) => {
                                setRentSelected(checked);
                                if (checked) setPenaltiesSelected(false);
                            }}
                            onOtherToggle={(checked) => {
                                setOtherSelected(checked);
                                if (checked) setPenaltiesSelected(false);
                            }}
                            onPenaltiesToggle={(checked) => {
                                setPenaltiesSelected(checked);
                                if (checked) {
                                    setRentSelected(false);
                                    setOtherSelected(false);
                                }
                            }}
                            onToggleSelectAll={handleSelectAll}
                            onContinue={handleStepOneContinue}
                        />
                    )}

                    {currentStep === SEQUENCE[1] && (
                        <PreviewInvoiceStep
                            options={options}
                            previewInvoices={filteredPreviewInvoices}
                            allPreviewInvoices={previewInvoices}
                            duplicateWarningsMap={duplicateWarningsMap}
                            currency={currency}
                            onEditAmount={handleEditAmount}
                            onEditDate={handleEditDate}
                            onDeleteRow={handleDeletePreviewRow}
                            onDeleteInvoiceItem={handleDeleteInvoiceItem}
                            onGenerate={handleGenerate}
                            searchText={searchText}
                            onSearchChange={setSearchText}
                            propertyOptions={uniquePropertiesFromPreview}
                            selectedPropertyId={selectedPropertyId}
                            onPropertyFilterChange={setSelectedPropertyId}
                            onClearFilters={() => {
                                setSearchText('');
                                setSelectedPropertyId('');
                            }}
                        />
                    )}

                    {currentStep === SEQUENCE[2] && (
                        <SendInvoiceStep
                            options={options}
                            settings={settingsState}
                            createdInvoiceIds={createdInvoiceIds}
                            deliveryOption={deliveryOption}
                            scheduleDate={scheduleDate}
                            onSetDeliveryOption={setDeliveryOption}
                            onScheduleDateChange={setScheduleDate}
                            onSendInvoices={onSendInvoices}
                            onExit={handleClose}
                        />
                    )}

                    {currentStep !== SEQUENCE[0] && currentStep !== SEQUENCE[SEQUENCE.length - 1] && (
                        <IonButton
                            fill="outline"
                            onClick={_prevStep}
                            className="ion-margin-top"
                        >
                            Previous
                        </IonButton>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default BulkCreateInvoiceWizard;

